import { TimerOutlined, TimerOffOutlined } from '@mui/icons-material';
import { Alert, AlertProps, AlertTitle, Stack, useMediaQuery, useTheme } from '@mui/material';
import dayjs from 'dayjs';
import React, { useState, useEffect } from 'react';
import { gameStateDisplay, gameStatusEnum, gameStateBgColorMap, fullThaiDateFormat } from '../../configs/constants';
import PromptTypo from '../typography/typography_prompt';
import { GameSession } from '../../services/game_service';
import { sxCenter } from '../styled/styled_flex_container';

interface AlertGameProps {
    gameSession?: GameSession | undefined | null;
    size?: 'small' | 'medium' | 'large';
    fullWidth?: boolean;
    variant?: AlertProps['variant'];
    severity?: AlertProps['severity'];
}

const AlertGame: React.FC<AlertGameProps> = ({
    gameSession,
    fullWidth = false,
    variant = 'filled',
    severity = 'alert',
    size = 'medium', // Default size is 'medium'
}): any => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const isMediumScreen = useMediaQuery(theme.breakpoints.between('sm', 'md'));

    // Adjust font sizes based on the size prop
    const titleFontSize = size === 'small' ? '1rem' : size === 'large' ? '1.8rem' : '1.5rem';
    const bodyFontSize = size === 'small' ? '0.8rem' : size === 'large' ? '1.2rem' : '1rem';

    // Adjust layout styles based on the size prop
    const alertPadding = size === 'small' ? '8px' : size === 'large' ? '24px' : '16px';
    const alertMargin = size === 'small' ? '4px' : size === 'large' ? '16px' : '8px';
    const iconSize = size === 'small' ? '20px' : size === 'large' ? '40px' : '30px';

    const currentGameState = gameSession?.state;
    const currentGameStateDisplay = gameStateDisplay[currentGameState ?? ''];
    const isGameOpened = currentGameState === gameStatusEnum.opened;
    const bgAlert = gameStateBgColorMap[currentGameState ?? ''];

    const alertIcon = isGameOpened ? (
        <TimerOutlined sx={{ fontSize: iconSize }} />
    ) : (
        <TimerOffOutlined sx={{ fontSize: iconSize }} />
    );

    const renderBySize = () => {
        if (size === 'medium') {
            return (
                <Alert
                    icon={alertIcon}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: alertPadding,
                        margin: alertMargin,
                        color: 'white',
                        width: fullWidth ? '100%' : 'auto',
                    }}
                    variant={variant}
                    severity={bgAlert}
                >
                    <AlertTitle>
                        <PromptTypo variant="h5" sx={{ fontSize: titleFontSize }}>
                            หวยรัฐบาลไทย - งวดวัน{dayjs(gameSession?.endGameDatetime).format(fullThaiDateFormat)}
                        </PromptTypo>
                    </AlertTitle>
                    <PromptTypo variant="body1" sx={{ fontSize: bodyFontSize }}>
                        สถานะ : {currentGameStateDisplay}
                    </PromptTypo>
                </Alert>
            );
        }
        if (size === 'small') {
            return (
                <Alert
                    icon={alertIcon}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '4px',
                        margin: '2px',
                        color: 'white',
                    }}
                    variant={variant}
                    severity={bgAlert}
                >
                    <PromptTypo variant="subtitle2" sx={{ fontSize: titleFontSize }}>
                        หวยรัฐบาลไทย - งวดวัน{dayjs(gameSession?.endGameDatetime).format(fullThaiDateFormat)}
                    </PromptTypo>
                    <PromptTypo variant="body2" sx={{ fontSize: '0.7rem' }}>
                        สถานะ :   {currentGameStateDisplay}
                    </PromptTypo>
                </Alert>
            );
        }
        if (size === 'large') {
            return (
                <Alert
                    icon={alertIcon}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: alertPadding,
                        margin: alertMargin,
                        color: 'white',
                        width: fullWidth ? '100%' : 'auto',
                    }}
                    variant={variant}
                    severity={bgAlert}
                >
                    <Stack direction={'row'} sx={{ ...sxCenter }}>
                        <AlertTitle>
                            <PromptTypo variant="h5" sx={{ fontSize: titleFontSize }}>
                                หวยรัฐบาลไทย - งวดวัน{dayjs(gameSession?.endGameDatetime).format(fullThaiDateFormat)}
                            </PromptTypo>
                            <PromptTypo variant="body1" sx={{ fontSize: bodyFontSize }}>
                                {currentGameStateDisplay}
                            </PromptTypo>
                        </AlertTitle>
                    </Stack>
                </Alert>
            );
        }

    };

    return renderBySize();
};

export default AlertGame;
